import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { ImageUpload } from '../../components/shared/ImageUpload';

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image_url: '' });

  useEffect(() => { loadCategories(); }, []);

  async function loadCategories() {
    try {
      const { data } = await api.get('/admin/categories');
      setCategories(data);
    } catch {} finally { setLoading(false); }
  }

  function openCreate() { setEditing(null); setForm({ name: '', description: '', image_url: '' }); setDialogOpen(true); }
  function openEdit(cat) { setEditing(cat); setForm({ name: cat.name, description: cat.description || '', image_url: cat.image_url || '' }); setDialogOpen(true); }

  async function handleSave() {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, form);
        toast.success('Category updated');
      } else {
        await api.post('/admin/categories', form);
        toast.success('Category created');
      }
      setDialogOpen(false);
      loadCategories();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/admin/categories/${id}`);
      toast.success('Deleted');
      loadCategories();
    } catch (err) { toast.error('Failed to delete'); }
  }

  return (
    <div data-testid="categories-management">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A]">Categories</h1>
        <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={openCreate} data-testid="create-category-btn">
          <Plus size={16} className="mr-2" /> Add Category
        </Button>
      </div>

      <Card className="border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-[#52525B]">Loading...</TableCell></TableRow>
            ) : categories.length === 0 ? (
              <TableRow><TableCell colSpan={3} className="text-center py-8 text-[#52525B]">No categories found</TableCell></TableRow>
            ) : categories.map(cat => (
              <TableRow key={cat.id} data-testid={`cat-row-${cat.id}`}>
                <TableCell className="font-medium">{cat.name}</TableCell>
                <TableCell className="text-[#52525B] text-sm">{cat.description || '-'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.id}`}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(cat.id)} data-testid={`delete-cat-${cat.id}`}><Trash2 size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">{editing ? 'Edit Category' : 'Create Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Banking" data-testid="cat-name-input" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" data-testid="cat-desc-input" /></div>
            <div><Label>Image</Label><ImageUpload value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} folder="categories" /><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Or paste URL..." className="mt-2 text-xs" data-testid="cat-image-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#7551e8] hover:bg-[#5e3dc7] text-white" onClick={handleSave} data-testid="save-category-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
